import { GridColDef, useGridApiContext } from "@mui/x-data-grid-pro";
import { formatCurrency } from "@poseidon/utils";
import { GridRenderEditCellParams } from "@mui/x-data-grid-pro";
import React from "react";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";

const expenditureWorkingsKeys = new Set([
  "totalExpenditure",
  "inflationBuffer",
  "totalAssessedExpenditure",
]);

const isExpenditureRow = (row: any) => {
  if (
    row.section === "currentMonthlyCommitments" ||
    row.section === "monthlyLivingCosts" ||
    row.section === "newBoatCosts"
  ) {
    return true;
  }
  if (row.section === "workings" && expenditureWorkingsKeys.has(row.key)) {
    return true;
  }
  return false;
};

const cellRenderer: GridColDef["renderCell"] = ({ value, row, field }) => {
  if (row.key === "isAffordable") {
    if (field === "variance") {
      return "";
    }
    return typeof value === "boolean" ? (value ? "Yes" : "No") : "";
  }
  if (row.key === "numberOfDependants") {
    return value;
  }
  if (field === "variance") {
    if (value === null || row.rowType === "heading") {
      return "";
    }
    const isBad = isExpenditureRow(row) ? value > 0 : value < 0;
    return (
      <span style={{ color: isBad ? "#d32f2f" : "#2e7d32" }}>
        {(value > 0 ? "+" : "") + formatCurrency(value)}
      </span>
    );
  }
  return formatCurrency(value);
};

function CustomEditComponent(props: GridRenderEditCellParams) {
  const { id, value, field, row } = props;
  const apiRef = useGridApiContext();

  if (row.key === "isAffordable") {
    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;
      apiRef.current.setEditCellValue({ id, field, value: newValue });
    };
    return (
      <Checkbox
        size="small"
        checked={!!value}
        inputProps={{ autoFocus: true }}
        onChange={handleValueChange}
      />
    );
  } else {
    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue =
        event.target.value === "" ? "" : parseFloat(event.target.value);
      apiRef.current.setEditCellValue({ id, field, value: newValue });
    };
    return (
      <TextField
        // variant="outlined"
        size="small"
        margin="none"
        value={value}
        inputProps={{
          autoFocus: true,
        }}
        onChange={handleValueChange}
        sx={{
          "& input": {
            textAlign: "right",
          },
        }}
        onFocus={(event) => event.target.select()}
      />
    );
  }
}

export const affordabilityColumns: (GridColDef & { colSpan?: any })[] = [
  {
    field: "id",
    width: 0,
    disableReorder: true,
  },
  {
    field: "key",
    width: 300,
    valueGetter: ({ row }) => row.id,
  },
  {
    field: "title",
    headerName: "Field Name",
    width: 280,
    sortable: false,
    disableReorder: true,
  },
  {
    field: "declared",
    headerName: "Declared",
    width: 180,
    align: "right",
    colSpan: ({ row }: { row: any }) => {
      if (
        row.key === "income" ||
        row.key === "currentMonthlyCommitments" ||
        row.key === "monthlyLivingCosts" ||
        row.key === "newBoatCosts"
      ) {
        return 3;
      }
      return undefined;
    },
    renderCell: cellRenderer,
    sortable: false,
    pinnable: false,
    hideable: false,
  },
  {
    field: "openBanking",
    headerName: "Open Banking",
    width: 180,
    align: "right",
    renderCell: cellRenderer,
    sortable: false,
  },
  {
    field: "variance",
    headerName: "Variance",
    width: 180,
    align: "right",
    renderCell: cellRenderer,
    sortable: false,
  },
  {
    field: "manual",
    headerName: "Manual",
    width: 180,
    align: "right",
    renderCell: cellRenderer,
    sortable: false,
    editable: true,
    renderEditCell: (params: GridRenderEditCellParams) => (
      <CustomEditComponent {...params} />
    ),
  },
];
